import React, { useState, useEffect } from 'react';
import { logError } from '@/lib/error-handler';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Progress } from '@/components/ui/progress';
import {
  Play,
  Pause,
  Settings,
  AlertCircle,
  CheckCircle,
  Zap,
  TestTube,
  Send,
  Clock,
  Activity,
} from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { workflowAutomationService } from '@/services/automation/workflowAutomationService';

interface Workflow {
  id: string;
  name: string;
  description: string;
  isActive: boolean;
  environment: 'test' | 'live';
  lastRun?: string;
  status: 'idle' | 'running' | 'success' | 'error';
  executionCount: number;
  errorCount: number;
}

export default function SimplifiedAutomation() {
  const [workflows, setWorkflows] = useState<Workflow[]>([]);
  const [environment, setEnvironment] = useState<'test' | 'live'>('test');
  const [isLoading, setIsLoading] = useState(true);
  const { toast } = useToast();

  // Load workflows
  useEffect(() => {
    loadWorkflows();
  }, []);

  const loadWorkflows = async () => {
    try {
      setIsLoading(true);
      const rules = await workflowAutomationService.getWorkflowRules();

      // Transform to simplified workflow format
      const simplifiedWorkflows: Workflow[] = rules.map((rule) => ({
        id: rule.id,
        name: rule.name,
        description: rule.description,
        isActive: rule.is_active,
        environment: 'test', // Default to test
        lastRun: rule.last_triggered,
        status: 'idle',
        executionCount: rule.execution_count,
        errorCount: 0,
      }));

      setWorkflows(simplifiedWorkflows);
    } catch (error) {
      logError('Error loading workflows:', error);
      toast({
        title: 'Error',
        description: 'Failed to load workflows',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Toggle workflow active state
  const toggleWorkflow = async (workflowId: string) => {
    const workflow = workflows.find((w) => w.id === workflowId);
    if (!workflow) return;

    try {
      await workflowAutomationService.updateWorkflowRule(workflowId, {
        is_active: !workflow.isActive,
      });

      setWorkflows((prev) =>
        prev.map((w) => (w.id === workflowId ? { ...w, isActive: !w.isActive } : w))
      );

      toast({
        title: workflow.isActive ? 'Workflow Paused' : 'Workflow Activated',
        description: `${workflow.name} is now ${!workflow.isActive ? 'active' : 'paused'}`,
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to update workflow',
        variant: 'destructive',
      });
    }
  };

  // Test workflow
  const testWorkflow = async (workflowId: string) => {
    const workflow = workflows.find((w) => w.id === workflowId);
    if (!workflow) return;

    // Update status to running
    setWorkflows((prev) =>
      prev.map((w) => (w.id === workflowId ? { ...w, status: 'running' } : w))
    );

    try {
      // Execute in test mode
      await workflowAutomationService.executeWorkflow(workflowId, {}, true);

      // Simulate execution time
      await new Promise((resolve) => setTimeout(resolve, 2000));

      // Update status to success
      setWorkflows((prev) =>
        prev.map((w) =>
          w.id === workflowId
            ? {
                ...w,
                status: 'success',
                lastRun: new Date().toISOString(),
                executionCount: w.executionCount + 1,
              }
            : w
        )
      );

      toast({
        title: 'Test Successful',
        description: `${workflow.name} executed successfully in test mode`,
      });

      // Reset status after 3 seconds
      setTimeout(() => {
        setWorkflows((prev) =>
          prev.map((w) => (w.id === workflowId ? { ...w, status: 'idle' } : w))
        );
      }, 3000);
    } catch (error) {
      setWorkflows((prev) =>
        prev.map((w) =>
          w.id === workflowId ? { ...w, status: 'error', errorCount: w.errorCount + 1 } : w
        )
      );

      toast({
        title: 'Test Failed',
        description: 'Workflow encountered an error during testing',
        variant: 'destructive',
      });
    }
  };

  // Get filtered workflows by environment
  const filteredWorkflows = workflows.filter(
    (w) => environment === 'test' || (environment === 'live' && w.isActive)
  );

  // Calculate stats
  const stats = {
    total: workflows.length,
    active: workflows.filter((w) => w.isActive).length,
    testReady: workflows.filter((w) => w.executionCount > 0 && w.errorCount === 0).length,
    needsAttention: workflows.filter((w) => w.errorCount > 0).length,
  };

  return (
    <div className="container mx-auto p-6 space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold">Automation Center</h1>
        <p className="text-gray-600 mt-1">Test and deploy automated workflows for your business</p>
      </div>

      {/* Environment Selector */}
      <Card>
        <CardHeader>
          <CardTitle>Environment</CardTitle>
          <CardDescription>Test your automations safely before going live</CardDescription>
        </CardHeader>
        <CardContent>
          <Tabs value={environment} onValueChange={(v) => setEnvironment(v as 'test' | 'live')}>
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="test" className="flex items-center gap-2">
                <TestTube className="w-4 h-4" />
                Test Environment
              </TabsTrigger>
              <TabsTrigger value="live" className="flex items-center gap-2">
                <Send className="w-4 h-4" />
                Live Environment
              </TabsTrigger>
            </TabsList>
          </Tabs>

          {environment === 'test' && (
            <Alert className="mt-4">
              <TestTube className="h-4 w-4" />
              <AlertTitle>Test Mode Active</AlertTitle>
              <AlertDescription>
                Workflows will run without sending real emails or making actual changes. Perfect for
                testing your automation logic.
              </AlertDescription>
            </Alert>
          )}

          {environment === 'live' && (
            <Alert className="mt-4" variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>Live Mode Active</AlertTitle>
              <AlertDescription>
                Workflows will execute real actions. Only tested and verified workflows should be
                activated in live mode.
              </AlertDescription>
            </Alert>
          )}
        </CardContent>
      </Card>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="text-2xl font-bold">{stats.total}</div>
            <p className="text-xs text-muted-foreground">Total Workflows</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-2xl font-bold">{stats.active}</div>
            <p className="text-xs text-muted-foreground">Active</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-2xl font-bold">{stats.testReady}</div>
            <p className="text-xs text-muted-foreground">Ready for Live</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-2xl font-bold">{stats.needsAttention}</div>
            <p className="text-xs text-muted-foreground">Needs Attention</p>
          </CardContent>
        </Card>
      </div>

      {/* Workflows */}
      <div className="space-y-4">
        <h2 className="text-xl font-semibold">
          {environment === 'test' ? 'Available Workflows' : 'Live Workflows'}
        </h2>

        {isLoading ? (
          <div className="text-center py-8">
            <Activity className="w-8 h-8 animate-spin mx-auto mb-2" />
            <p className="text-gray-600">Loading workflows...</p>
          </div>
        ) : filteredWorkflows.length === 0 ? (
          <Card>
            <CardContent className="text-center py-8">
              <Zap className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600">
                {environment === 'test'
                  ? 'No workflows available. Create your first automation!'
                  : 'No workflows are currently live. Test and activate workflows first.'}
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 gap-4">
            {filteredWorkflows.map((workflow) => (
              <Card
                key={workflow.id}
                className={workflow.status === 'running' ? 'border-blue-500' : ''}
              >
                <CardContent className="p-6">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <h3 className="text-lg font-semibold">{workflow.name}</h3>
                        {workflow.status === 'running' && (
                          <Badge variant="default" className="animate-pulse">
                            Running
                          </Badge>
                        )}
                        {workflow.status === 'success' && (
                          <Badge variant="secondary" className="bg-green-100">
                            <CheckCircle className="w-3 h-3 mr-1" />
                            Success
                          </Badge>
                        )}
                        {workflow.status === 'error' && (
                          <Badge variant="destructive">
                            <AlertCircle className="w-3 h-3 mr-1" />
                            Error
                          </Badge>
                        )}
                      </div>
                      <p className="text-gray-600 mb-4">{workflow.description}</p>

                      <div className="flex items-center gap-4 text-sm text-gray-500">
                        <span className="flex items-center gap-1">
                          <Activity className="w-4 h-4" />
                          {workflow.executionCount} runs
                        </span>
                        {workflow.lastRun && (
                          <span className="flex items-center gap-1">
                            <Clock className="w-4 h-4" />
                            Last run: {new Date(workflow.lastRun).toLocaleString()}
                          </span>
                        )}
                        {workflow.errorCount > 0 && (
                          <span className="flex items-center gap-1 text-red-600">
                            <AlertCircle className="w-4 h-4" />
                            {workflow.errorCount} errors
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="flex items-center gap-2 ml-4">
                      {environment === 'test' ? (
                        <>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => testWorkflow(workflow.id)}
                            disabled={workflow.status === 'running'}
                          >
                            {workflow.status === 'running' ? (
                              <>
                                <Activity className="w-4 h-4 mr-1 animate-spin" />
                                Testing...
                              </>
                            ) : (
                              <>
                                <Play className="w-4 h-4 mr-1" />
                                Test
                              </>
                            )}
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              // Navigate to workflow builder
                              window.location.href = `/automation/edit/${workflow.id}`;
                            }}
                          >
                            <Settings className="w-4 h-4" />
                          </Button>
                        </>
                      ) : (
                        <Switch
                          checked={workflow.isActive}
                          onCheckedChange={() => toggleWorkflow(workflow.id)}
                        />
                      )}
                    </div>
                  </div>

                  {workflow.status === 'running' && <Progress value={50} className="mt-4" />}
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Create New Button */}
      {environment === 'test' && (
        <div className="fixed bottom-6 right-6">
          <Button
            size="lg"
            onClick={() => (window.location.href = '/automation/create')}
            className="shadow-lg"
          >
            <Zap className="w-5 h-5 mr-2" />
            Create Workflow
          </Button>
        </div>
      )}
    </div>
  );
}
